import { useState, type FormEvent } from 'react';
import { addRsvp } from '@/lib/weddingApi';

interface Props {
  invitationId: string;
  interactive: boolean;
}

type Status = 'idle' | 'sending' | 'done' | 'error';

export default function RsvpBlock({ invitationId, interactive }: Props) {
  const [side, setSide] = useState('신랑');
  const [attendance, setAttendance] = useState('참석');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [guests, setGuests] = useState('1명');
  const [meal, setMeal] = useState('식사 예약');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const disabled = !interactive || status === 'done';

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!interactive) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setStatus('error');
      setError('성함을 입력해 주세요.');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      await addRsvp({
        invitationId,
        name: trimmed,
        side,
        phone,
        attendance,
        guests,
        meal: attendance === '참석' ? meal : '',
        message: '',
      });
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError(
        err instanceof Error
          ? `전송에 실패했어요. ${err.message}`
          : '전송에 실패했어요. 잠시 후 다시 시도해 주세요.',
      );
    }
  };

  return (
    <section className="inv-sec" id="rsvp">
      <p className="inv-kicker">R.S.V.P</p>
      <h3 className="inv-sec-title">참석 의사 체크하기</h3>
      <p className="inv-sec-sub">
        한 분 한 분을 소중히 모실 수 있도록
        <br />
        참석 의사를 전해 주시면 감사하겠습니다.
      </p>

      {status === 'done' ? (
        <div className="inv-note ok">
          <i className="ri-heart-3-line" aria-hidden="true" />
          <p>참석 의사가 전달됐어요. 소중한 마음 감사합니다.</p>
        </div>
      ) : (
        <form className="inv-form" onSubmit={submit}>
          <div className="inv-field">
            <label>
              어느 분의 하객이신가요? <span className="req">*</span>
            </label>
            <div className="inv-choice">
              {['신랑', '신부'].map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={disabled}
                  className={`inv-choice-btn ${side === v ? 'is-active' : ''}`}
                  onClick={() => setSide(v)}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="inv-field">
            <label>
              참석하실 수 있나요? <span className="req">*</span>
            </label>
            <div className="inv-choice">
              <button
                type="button"
                disabled={disabled}
                className={`inv-choice-btn ${attendance === '참석' ? 'is-active' : ''}`}
                onClick={() => setAttendance('참석')}
              >
                참석할게요
              </button>
              <button
                type="button"
                disabled={disabled}
                className={`inv-choice-btn ${attendance === '불참' ? 'is-active' : ''}`}
                onClick={() => setAttendance('불참')}
              >
                참석이 어려워요
              </button>
            </div>
          </div>

          <div className="inv-field">
            <label htmlFor="rsvp-name">
              성함이 어떻게 되시나요? <span className="req">*</span>
            </label>
            <input
              id="rsvp-name"
              className="inv-input"
              value={name}
              disabled={disabled}
              maxLength={20}
              onChange={(e) => setName(e.target.value)}
              placeholder="참석자 본인 성함"
            />
          </div>

          {attendance === '참석' ? (
            <div className="inv-field-row">
              <div className="inv-field">
                <label htmlFor="rsvp-guests">참석 인원</label>
                <select
                  id="rsvp-guests"
                  className="inv-input"
                  value={guests}
                  disabled={disabled}
                  onChange={(e) => setGuests(e.target.value)}
                >
                  <option value="1명">1명</option>
                  <option value="2명">2명</option>
                  <option value="3명">3명</option>
                  <option value="4명">4명</option>
                  <option value="5명 이상">5명 이상</option>
                </select>
              </div>
              <div className="inv-field">
                <label htmlFor="rsvp-meal">식사 여부</label>
                <select
                  id="rsvp-meal"
                  className="inv-input"
                  value={meal}
                  disabled={disabled}
                  onChange={(e) => setMeal(e.target.value)}
                >
                  <option value="식사 예약">식사 예약함</option>
                  <option value="식사 안 함">식사 예약 안 함</option>
                  <option value="미정">아직 미정</option>
                </select>
              </div>
            </div>
          ) : null}

          <div className="inv-field">
            <label htmlFor="rsvp-phone">동명이인 체크를 위한 번호를 알려주세요</label>
            <input
              id="rsvp-phone"
              className="inv-input"
              value={phone}
              disabled={disabled}
              inputMode="tel"
              maxLength={20}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="핸드폰 번호 뒤 4자리"
            />
          </div>

          {status === 'error' ? (
            <p className="inv-note err">
              <i className="ri-error-warning-line" aria-hidden="true" />
              {error}
            </p>
          ) : null}

          <button type="submit" className="inv-submit" disabled={disabled}>
            {status === 'sending' ? '전하는 중…' : '참석 의사 전하기'}
            <i className="ri-heart-line" aria-hidden="true" />
          </button>

          {!interactive ? (
            <p className="inv-form-hint">미리보기 화면이에요. 실제 청첩장에서 전송할 수 있어요.</p>
          ) : null}
        </form>
      )}
    </section>
  );
}